import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ImportMenu = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [tab, setTab] = useState('text'); // 'text' | 'csv'
    const [rawText, setRawText] = useState('');
    const [parsedItems, setParsedItems] = useState([]);
    const [parseErrors, setParseErrors] = useState([]);
    
    // Default categories & types
    const [defaultCategory, setDefaultCategory] = useState('General');
    const [defaultType, setDefaultType] = useState('veg');

    // CSV state
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState(''); // 'success', 'error', 'info'
    const [isUploading, setIsUploading] = useState(false);
    
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, navigate]);

    const fetchCustomers = async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_customers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                const sorted = data.customers.sort((a, b) => a.restaurant_name.localeCompare(b.restaurant_name));
                setCustomers(sorted);
            }
        } catch (err) {
            console.error('Failed to fetch customers', err);
        }
    };

    // ── CSV Parsing Function ──
    const parseCSVText = (text) => {
        const lines = text.split(/\r?\n/);
        const items = [];
        const errors = [];
        let rowNum = 0;
        
        for (const line of lines) {
            rowNum++;
            const trimmed = line.trim();
            if (!trimmed) continue;
            if (rowNum <= 2) continue; // skip header columns and instructions
            
            // Basic CSV row parsing (handles commas inside quotes)
            const fields = [];
            let currentField = '';
            let inQuotes = false;
            
            for (let c = 0; c < trimmed.length; c++) {
                const char = trimmed[c];
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    fields.push(currentField.trim());
                    currentField = '';
                } else {
                    currentField += char;
                }
            }
            fields.push(currentField.trim());
            
            const name = fields[0] || '';
            const category = fields[1] || '';
            const item_type = (fields[2] || 'veg').toLowerCase();
            const description = fields[3] || '';
            const singlePrice = parseFloat(fields[4]) || 0;
            
            const size1Name = fields[5] || '';
            const size1Price = parseFloat(fields[6]) || 0;
            const size2Name = fields[7] || '';
            const size2Price = parseFloat(fields[8]) || 0;
            const size3Name = fields[9] || '';
            const size3Price = parseFloat(fields[10]) || 0;
            
            if (!name) {
                errors.push(`Row ${rowNum}: Item Name is required.`);
                continue;
            }
            if (!category) {
                errors.push(`Row ${rowNum}: Category is required for '${name}'.`);
                continue;
            }
            
            const sizes = [];
            if (size1Name && size1Price > 0) sizes.push({ size_name: size1Name, price: size1Price });
            if (size2Name && size2Price > 0) sizes.push({ size_name: size2Name, price: size2Price });
            if (size3Name && size3Price > 0) sizes.push({ size_name: size3Name, price: size3Price });
            
            items.push({
                id: `${Date.now()}_${Math.random()}_${rowNum}`,
                name,
                category,
                item_type: ['veg', 'non-veg', 'addon'].includes(item_type) ? item_type : 'veg',
                description,
                price: sizes.length > 0 ? 0 : singlePrice,
                sizes: sizes.length > 0 ? sizes : null
            });
        }
        return { items, errors };
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                const { items, errors } = parseCSVText(text);
                setParsedItems(items);
                setParseErrors(errors);
                if (items.length > 0) {
                    setStatusMessage(`Successfully parsed ${items.length} items from CSV. Please review below.`);
                    setStatusType('success');
                } else {
                    setStatusMessage('Could not parse any valid items from the uploaded file.');
                    setStatusType('error');
                }
            };
            reader.readAsText(file);
        }
    };

    // ── Text Parsing Function ──
    const parseLine = (line, globalCategory, globalType) => {
        line = line.trim();
        if (!line) return null;

        let category = globalCategory || 'General';
        let item_type = globalType || 'veg';

        // Extract inline category: [Starters] Paneer Tikka - 220
        const catMatch = line.match(/^\[([^\]]+)\]\s*/);
        if (catMatch) {
            category = catMatch[1].trim();
            line = line.slice(catMatch[0].length);
        }

        // Extract description after pipe: Item Name - 220 | Some description
        let description = '';
        const pipeIdx = line.indexOf('|');
        if (pipeIdx !== -1) {
            description = line.slice(pipeIdx + 1).trim();
            line = line.slice(0, pipeIdx).trim();
        }

        // Split name and price
        const parts = line.split(/\s*[-;,]\s*/);
        if (parts.length < 2) {
            return { error: `Missing separator (e.g. '-') between name and price in: "${line}"`, raw: line };
        }

        const priceStr = parts[parts.length - 1].replace(/[^0-9.]/g, '');
        const nameStr  = parts.slice(0, parts.length - 1).join('-').trim();

        if (!nameStr) return { error: `Missing item name.`, raw: line };
        if (!priceStr || isNaN(parseFloat(priceStr))) {
            return { error: `Invalid price in: "${line}"`, raw: line };
        }

        return {
            id: `${Date.now()}_${Math.random()}`,
            name: nameStr,
            price: parseFloat(priceStr),
            category,
            item_type,
            description,
            sizes: null
        };
    };

    const handleParseText = () => {
        const lines = rawText.split('\n').filter(l => l.trim() !== '');
        const newItems = [];
        const newErrors = [];

        lines.forEach((line, i) => {
            const result = parseLine(line, defaultCategory, defaultType);
            if (!result) return;
            if (result.error) {
                newErrors.push(`Line ${i + 1}: ${result.error}`);
            } else {
                newItems.push(result);
            }
        });

        setParsedItems(prev => [...prev, ...newItems]);
        setParseErrors(newErrors);
        setRawText('');
        if (newItems.length > 0) {
            setStatusMessage(`Parsed ${newItems.length} text items successfully. Please review below.`);
            setStatusType('success');
        } else {
            setStatusMessage('Failed to parse text. Check the guide format.');
            setStatusType('error');
        }
    };

    // ── Row Actions ──
    const removeItem = (id) => setParsedItems(p => p.filter(i => i.id !== id));
    const duplicateItem = (id) => {
        const idx = parsedItems.findIndex(i => i.id === id);
        if (idx === -1) return;
        const copy = { ...parsedItems[idx], id: `${Date.now()}_${Math.random()}` };
        const next = [...parsedItems];
        next.splice(idx + 1, 0, copy);
        setParsedItems(next);
    };
    const updateItem = (id, field, value) => {
        setParsedItems(p => p.map(i => i.id === id ? { ...i, [field]: value } : i));
    };

    const handleDownloadTemplate = () => {
        const url = `${BASE}/apimenu/v2/v2_menu_template.php?action=download`;
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', 'menu_template.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleSaveMenu = async () => {
        if (!selectedCustomer) {
            setStatusMessage('Please select a customer / restaurant.');
            setStatusType('error');
            return;
        }
        if (parsedItems.length === 0) {
            setStatusMessage('No parsed items to upload.');
            setStatusType('error');
            return;
        }

        setIsUploading(true);
        setStatusMessage('Importing menu items to database...');
        setStatusType('info');

        try {
            const response = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=import_menu_items`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customer_id: selectedCustomer,
                    items: parsedItems
                })
            });

            const result = await response.json();

            if (result.success) {
                setStatusMessage(result.message);
                setStatusType('success');
                setParsedItems([]);
                setParseErrors([]);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                throw new Error(result.message || 'An unknown error occurred.');
            }
        } catch (error) {
            setStatusMessage(`Failed to save: ${error.message}`);
            setStatusType('error');
        } finally {
            setIsUploading(false);
        }
    };

    const getStatusStyle = () => {
        if (statusType === 'error') return { color: 'var(--danger)', background: 'var(--danger-bg)', border: '1px solid var(--danger)' };
        if (statusType === 'success') return { color: 'var(--success)', background: 'var(--success-bg)', border: '1px solid var(--success)' };
        if (statusType === 'info') return { color: 'var(--info)', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)' };
        return {};
    };

    const tabBtnStyle = (isActive) => ({
        padding: '10px 20px',
        border: 'none',
        borderBottom: isActive ? '3px solid var(--accent-primary, #ea580c)' : '3px solid transparent',
        background: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
        color: isActive ? 'var(--accent-primary, #ea580c)' : 'var(--text-secondary, #6b7280)',
        fontSize: '0.95em',
        transition: 'color 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    });

    return (
        <SuperAdminLayout>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h2 className="sa-page-title">Bulk Import Menu Items</h2>
                        <p className="sa-page-subtitle">Upload text or CSV templates to instantly set up menus for partners.</p>
                    </div>
                    <button onClick={handleDownloadTemplate} className="sa-primary-btn" style={{ background: 'var(--success)' }}>
                        <i className="fas fa-file-download" style={{ marginRight: '6px' }} /> Download Template
                    </button>
                </div>

                <div className="sa-panel" style={{ padding: '2rem' }}>
                    {/* Customer Selection */}
                    <div className="sa-form-group">
                        <label className="sa-form-label" style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 600 }}>
                            1. Select Partner / Restaurant
                        </label>
                        <select 
                            className="sa-select" 
                            style={{ width: '100%', background: 'var(--bg-card)' }} 
                            value={selectedCustomer} 
                            onChange={(e) => setSelectedCustomer(e.target.value)}
                        >
                            <option value="">-- Choose a Restaurant --</option>
                            {customers.map(c => (
                                <option key={c.id} value={c.id}>{c.restaurant_name} (@{c.username})</option>
                            ))}
                        </select>
                    </div>

                    {/* Tab Navigation */}
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e5e7eb)', marginBottom: '20px', marginTop: '25px' }}>
                        <button style={tabBtnStyle(tab === 'text')} onClick={() => { setTab('text'); setParsedItems([]); }}>
                            <i className="fas fa-keyboard" /> Paste Raw Text
                        </button>
                        <button style={tabBtnStyle(tab === 'csv')} onClick={() => { setTab('csv'); setParsedItems([]); }}>
                            <i className="fas fa-file-csv" /> Upload CSV File
                        </button>
                    </div>

                    {/* Paste Raw Text Tab */}
                    {tab === 'text' && (
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                <div className="sa-form-group" style={{ marginBottom: 0 }}>
                                    <label className="sa-form-label">Default Category</label>
                                    <input 
                                        type="text" 
                                        className="sa-input" 
                                        value={defaultCategory} 
                                        onChange={e => setDefaultCategory(e.target.value)} 
                                        placeholder="e.g. Starters"
                                    />
                                </div>
                                <div className="sa-form-group" style={{ marginBottom: 0 }}>
                                    <label className="sa-form-label">Default Type</label>
                                    <select 
                                        className="sa-select" 
                                        value={defaultType} 
                                        onChange={e => setDefaultType(e.target.value)}
                                    >
                                        <option value="veg">🟢 Veg</option>
                                        <option value="non-veg">🔴 Non-Veg</option>
                                        <option value="addon">⚪ Add-on</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ background: 'var(--bg-dark, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px', padding: '15px', marginBottom: '20px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '5px' }}>Format Instructions:</strong>
                                Paste dishes one per line using the separator <kbd>-</kbd> to separate Name and Price.
                                <div style={{ marginTop: '5px', fontFamily: 'monospace', display: 'flex', flexDirection: 'column', gap: '2px', background: 'rgba(0,0,0,0.05)', padding: '8px', borderRadius: '4px' }}>
                                    <span>Item Name - Price</span>
                                    <span>[Category Name] Item Name - Price</span>
                                    <span>Item Name - Price | Description details</span>
                                </div>
                            </div>

                            <textarea
                                className="sa-input"
                                value={rawText}
                                onChange={e => setRawText(e.target.value)}
                                placeholder="e.g.&#10;Paneer Tikka - 220&#10;[Pizza] Margherita Pizza - 299 | Hand-tossed cheese pizza&#10;Masala Dosa - 120"
                                style={{ width: '100%', height: '180px', fontFamily: 'monospace', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
                            />

                            <button 
                                onClick={handleParseText} 
                                className="sa-primary-btn" 
                                disabled={!rawText.trim() || !selectedCustomer}
                                style={{ marginTop: '15px', width: '100%', padding: '12px' }}
                            >
                                <i className="fas fa-magic" style={{ marginRight: '6px' }} /> Parse Text Items
                            </button>
                        </div>
                    )}

                    {/* CSV File Import Tab */}
                    {tab === 'csv' && (
                        <div>
                            <div style={{ border: '2px dashed var(--border-color)', padding: '30px', borderRadius: '8px', textAlign: 'center', background: 'var(--bg-dark)', cursor: 'pointer' }}>
                                <i className="fas fa-cloud-upload-alt fa-3x" style={{ color: 'var(--accent-primary)', marginBottom: '10px' }}></i>
                                <input 
                                    type="file" 
                                    accept=".csv" 
                                    ref={fileInputRef} 
                                    onChange={handleFileChange} 
                                    disabled={!selectedCustomer}
                                    style={{ color: 'var(--text-secondary)', display: 'block', width: '100%', cursor: 'pointer' }}
                                />
                                <p style={{ color: 'var(--text-secondary)', margin: '10px 0 0 0', fontSize: '0.85rem' }}>
                                    Select the filled Excel / CSV template to import.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Feedback Status */}
                    {statusMessage && (
                        <div style={{ padding: '15px', borderRadius: '8px', marginTop: '20px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '10px', ...getStatusStyle() }}>
                            <i className={`fas ${statusType === 'error' ? 'fa-exclamation-triangle' : statusType === 'success' ? 'fa-check-circle' : 'fa-info-circle'}`}></i>
                            <span>{statusMessage}</span>
                        </div>
                    )}

                    {/* Parsing Errors */}
                    {parseErrors.length > 0 && (
                        <div style={{ marginTop: '20px', padding: '15px', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: '8px', fontSize: '0.85rem' }}>
                            <strong style={{ color: 'var(--danger)' }}>Skipped Rows/Errors:</strong>
                            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', color: 'var(--danger)' }}>
                                {parseErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                            </ul>
                        </div>
                    )}

                    {/* Parsed Items Preview Table */}
                    {parsedItems.length > 0 && (
                        <div style={{ marginTop: '30px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                                    Preview parsed menu items ({parsedItems.length})
                                </h3>
                                <button 
                                    onClick={() => { setParsedItems([]); setParseErrors([]); if (fileInputRef.current) fileInputRef.current.value = ''; }} 
                                    className="sa-secondary-btn" 
                                    style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                                >
                                    Clear List
                                </button>
                            </div>

                            <div className="sa-table-container">
                                <table className="sa-table">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th>Item Name</th>
                                            <th style={{ width: '120px' }}>Price (₹)</th>
                                            <th style={{ width: '140px' }}>Type</th>
                                            <th>Description</th>
                                            <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedItems.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <input 
                                                        type="text" 
                                                        className="sa-input" 
                                                        value={item.category} 
                                                        onChange={e => updateItem(item.id, 'category', e.target.value)} 
                                                        style={{ width: '100%', padding: '6px' }}
                                                    />
                                                </td>
                                                <td>
                                                    <input 
                                                        type="text" 
                                                        className="sa-input" 
                                                        value={item.name} 
                                                        onChange={e => updateItem(item.id, 'name', e.target.value)} 
                                                        style={{ width: '100%', padding: '6px' }}
                                                    />
                                                </td>
                                                <td>
                                                    {item.sizes && item.sizes.length > 0 ? (
                                                        <span style={{ fontSize: '0.8rem', color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                                                            {item.sizes.map(s => `${s.size_name}: ₹${s.price}`).join(', ')}
                                                        </span>
                                                    ) : (
                                                        <input 
                                                            type="number" 
                                                            className="sa-input" 
                                                            value={item.price} 
                                                            onChange={e => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)} 
                                                            style={{ width: '100%', padding: '6px' }}
                                                        />
                                                    )}
                                                </td>
                                                <td>
                                                    <select 
                                                        className="sa-select" 
                                                        value={item.item_type} 
                                                        onChange={e => updateItem(item.id, 'item_type', e.target.value)} 
                                                        style={{ width: '100%', padding: '6px' }}
                                                    >
                                                        <option value="veg">🟢 Veg</option>
                                                        <option value="non-veg">🔴 Non-Veg</option>
                                                        <option value="addon">⚪ Add-on</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input 
                                                        type="text" 
                                                        className="sa-input" 
                                                        value={item.description} 
                                                        onChange={e => updateItem(item.id, 'description', e.target.value)} 
                                                        placeholder="Optional description"
                                                        style={{ width: '100%', padding: '6px' }}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                        <button onClick={() => duplicateItem(item.id)} className="sa-action-btn success" title="Duplicate row" style={{ padding: '6px' }}>
                                                            <i className="fas fa-copy" />
                                                        </button>
                                                        <button onClick={() => removeItem(item.id)} className="sa-action-btn danger" title="Delete row" style={{ padding: '6px' }}>
                                                            <i className="fas fa-trash-alt" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button 
                                onClick={handleSaveMenu} 
                                className="sa-primary-btn" 
                                disabled={isUploading || !selectedCustomer}
                                style={{ width: '100%', marginTop: '20px', padding: '15px', fontSize: '1.05rem' }}
                            >
                                {isUploading ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }} /> Uploading items...</> : <><i className="fas fa-save" style={{ marginRight: '6px' }} /> Import Parsed Items to Restaurant</>}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </SuperAdminLayout>
    );
};

export default ImportMenu;
